import { useMemo, useState } from 'react';
import { admin, getConfig, readAccessToken } from '@repletoryreach/sdk-api';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { Stack, Button, Group, Text, Badge, SimpleGrid, Card } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, TableShell, DataTable, KpiFilterRow, type KpiFilterItem } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { StatCard } from '../../../components/StatCard';
import { TableFooter } from '../../../components/TableFooter';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatMinor, formatDateTime, formatInteger } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useAffiliateSummary, useAffiliateWithdrawals, useWithdrawalAction } from '../../../data/useAffiliates';
import { useT } from '../../../i18n/I18nProvider';

/**
 * The withdrawal queue: money about to leave the company's bank account.
 *
 * A row carries the bank details with the account number masked (owner's
 * decision of 2 October 2026): the full number is only in the bank file, and
 * each download of it is recorded. Beside them is `available after`: a negative figure means a
 * refund landed after the partner asked, and marking it paid is refused until
 * an operator has put the balance right.
 */
export default function PartnerWithdrawalsPage() {
  const t = useT();
  const summary = useAffiliateSummary();
  const [status, setStatus] = useState<string | undefined>('requested');
  const list = useAffiliateWithdrawals(status);
  const act = useWithdrawalAction();
  const [pageSize, setPageSize] = usePersistedState<number>('admin.partnerWithdrawals.pageSize', 25);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  const rows = useMemo(() => (list.data?.data ?? []).map((w, i) => ({ ...w, _i: i })), [list.data]);
  const paged = rows.slice((page - 1) * pageSize, page * pageSize);
  const current = rows.find((w) => w.id === open);
  const s = summary.data?.data;

  // Only the selected status is counted, because that is the only one this
  // page has fetched: a count on the others would be a guess.
  const kpis: KpiFilterItem[] = ['requested', 'approved', 'paid', 'rejected'].map((k) => ({
    id: k,
    label: t(`partners.withdrawalStatus.${k}`),
    value: k === status ? formatInteger(rows.length) : '',
    tone: k === 'requested' ? 'pending' : k === 'paid' ? 'healthy' : k === 'rejected' ? 'down' : 'neutral'
  }));

  async function run(action: 'approve' | 'reject' | 'paid') {
    if (!current) return;
    if (action === 'paid' && !reference.trim()) { notifyError(t('partners.withdrawals.referenceRequired')); return; }
    if (action === 'reject' && !note.trim()) { notifyError(t('partners.action.reasonRequired')); return; }
    try {
      await act.mutateAsync({
        id: current.id,
        action,
        reason: note.trim() || undefined,
        payment_reference: reference.trim() || undefined
      });
      notifySuccess(t(`partners.withdrawals.${action}Done`));
      setOpen(null);
      setReference('');
      setNote('');
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('partners.withdrawals.actionFailed'));
    }
  }

  async function downloadBankFile() {
    try {
      // The export is authenticated, so a plain link cannot open it: a browser
      // navigation carries no Authorization header. Same reasoning as the
      // invoice PDF source in Billing.
      const token = readAccessToken();
      const res = await fetch(`${getConfig().apiBaseUrl}${admin.affiliateWithdrawalsCsvPath('approved')}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error(String(res.status));
      downloadText(await res.text(), `partner-payouts-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
    } catch {
      notifyError(t('partners.withdrawals.exportFailed'));
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('partners.eyebrow')}
        title={t('partners.withdrawals.title')}
        description={t('partners.withdrawals.description')}
        actions={<Button variant="light" onClick={downloadBankFile}>{t('partners.withdrawals.export')}</Button>}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <StatCard label={t('partners.withdrawals.waiting')} value={formatInteger(s?.withdrawals_waiting ?? 0)} hint={t('partners.withdrawals.waitingHint')} tone={s && s.withdrawals_waiting > 0 ? 'warning' : 'neutral'} loading={summary.isLoading} />
        <StatCard label={t('partners.stat.owed')} value={formatMinor(s?.owed_minor ?? 0, 'ZAR')} hint={t('partners.stat.owedHint')} loading={summary.isLoading} />
        <StatCard label={t('partners.withdrawals.paidThisMonth')} value={formatMinor(s?.paid_this_month_minor ?? 0, 'ZAR')} hint={t('partners.withdrawals.paidThisMonthHint')} loading={summary.isLoading} />
      </SimpleGrid>

      <KpiFilterRow
        items={kpis}
        value={status ?? 'requested'}
        onChange={(next) => { setStatus(next ?? 'requested'); setPage(1); }}
        filteringLabel={t('common.filtering')}
      />

      <TableShell
        totalLabel={t('partners.withdrawals.total', { count: formatInteger(rows.length) })}
        loading={list.isLoading}
        isEmpty={!list.isLoading && rows.length === 0}
        empty={<EmptyState title={t('partners.withdrawals.emptyTitle')} description={t('partners.withdrawals.emptyBody')} />}
        error={list.isError ? (
          <Alert color="error" role="alert">
            {t('partners.withdrawals.loadFailed')}{' '}
            <Button variant="subtle" size="sm" onClick={() => void list.refetch()}>{t('action.retry')}</Button>
          </Alert>
        ) : undefined}
        pagination={<TableFooter page={page} pageSize={pageSize} total={rows.length} onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />}
      >
        <DataTable<Indexed<(typeof paged)[number]>>
          idAccessor="_i"
          records={paged}
          onRowClick={(r) => { setOpen(r.id); setReference(''); setNote(''); }}
          columns={[
            { accessor: 'affiliate_name', title: t('partners.column.partner'), render: (r) => r.affiliate_name ?? '—' },
            { accessor: 'amount_minor', title: t('partners.column.amount'), render: (r) => formatMinor(r.amount_minor, 'ZAR') },
            { accessor: 'method', title: t('partners.column.method'), render: (r) => t(`partners.method.${r.method}`, r.method) },
            { accessor: 'requested_at', title: t('partners.column.requested'), render: (r) => formatDateTime(r.requested_at) },
            {
              accessor: 'available_after_minor',
              title: t('partners.column.availableAfter'),
              render: (r) => r.available_after_minor < 0
                ? <Badge color="error">{formatMinor(r.available_after_minor, 'ZAR')}</Badge>
                : formatMinor(r.available_after_minor, 'ZAR')
            },
            { accessor: 'status', title: t('partners.column.status'), render: (r) => <Badge>{t(`partners.withdrawalStatus.${r.status}`, r.status)}</Badge> },
            { accessor: 'payment_reference', title: t('partners.column.reference'), render: (r) => r.payment_reference ?? r.review_note ?? '—' }
          ]}
        />
      </TableShell>

      <Drawer opened={Boolean(open)} onClose={() => setOpen(null)} position="right" size="md" title={t('partners.withdrawals.reviewTitle')}>
        {current && (
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="xs">
                <Text role="label">{current.affiliate_name}</Text>
                <Text role="bodySmall">{formatMinor(current.amount_minor, 'ZAR')} · {t(`partners.method.${current.method}`, current.method)}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(current.requested_at)}</Text>
              </Stack>
            </Card>

            {current.method === 'eft' && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text role="label">{t('partners.withdrawals.bankTitle')}</Text>
                  <Text role="bodySmall">{current.account_holder ?? '—'}</Text>
                  <Text role="bodySmall">{current.bank_name ?? '—'} · {current.branch_code ?? '—'}</Text>
                  <Text role="bodySmall">{current.account_number_masked ?? '—'}</Text>
                  {/* Masked for everyone who looks; the full number is in the bank file, and each download is recorded. */}
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('partners.withdrawals.fullNumberInFile')}</Text>
                </Stack>
              </Card>
            )}

            {current.available_after_minor < 0 && (
              <Alert color="error" role="alert">{t('partners.withdrawals.negative')}</Alert>
            )}

            {(current.status === 'requested' || current.status === 'approved') && (
              <Stack gap="md">
                <TextInput
                  label={t('partners.withdrawals.reference')}
                  description={t('partners.withdrawals.referenceHint')}
                  value={reference}
                  onChange={(e) => setReference(e.currentTarget.value)}
                />
                <Textarea
                  label={t('partners.withdrawals.note')}
                  description={t('partners.withdrawals.noteHint')}
                  value={note}
                  onChange={(e) => setNote(e.currentTarget.value)}
                  minRows={2}
                />
                <Group>
                  {current.status === 'requested' && (
                    <Button size="sm" loading={act.isPending} onClick={() => void run('approve')}>{t('partners.withdrawals.approve')}</Button>
                  )}
                  <Button size="sm" variant="light" loading={act.isPending} onClick={() => void run('paid')}>{t('partners.withdrawals.markPaid')}</Button>
                  <Button size="sm" variant="subtle" loading={act.isPending} onClick={() => void run('reject')}>{t('partners.withdrawals.reject')}</Button>
                </Group>
              </Stack>
            )}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
